import React, { useContext, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Box, Button, Link, Stack, Toolbar, Typography } from '@mui/material';

import { neutral } from '_lex/_foundations/phxColors';
import { AppContext } from '../../../../context';
import { translate as t } from 'hooks/useTranslations';
import useGroupUsers from 'views/App/Portal/LexID/Groups/_data_sources/useGroupUsers';
import useManageGroupUser from 'views/App/Portal/LexID/Groups/_data_sources/useManageGroupUser';
import type { User } from 'views/App/Portal/LexID/Users/_types/user';
import ListSection from 'components/_layouts/ListSection';
import { ActionMenu, QuickSearch } from 'components/_atoms';
import AddUser from './AddUser';

const columnHelper = createColumnHelper<User>();

type UsersProps = {
  onAddingUsersChange?: (value: boolean) => void;
};

export default function Users({ onAddingUsersChange }: UsersProps) {
  const { currentOrganizationId } = useContext(AppContext);

  const { id } = useParams();
  const usersQuery = useGroupUsers(currentOrganizationId || '', id || '');
  const queryClient = useQueryClient();
  const [addingUsers, setAddingUsers] = useState<boolean>(false);
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [step, setStep] = useState<'select' | 'review'>('select');

  const updateAddingUsers = (value: boolean) => {
    setAddingUsers(value);
    onAddingUsersChange?.(value);
    if (!value) setStep('select');
  };
  const submitRef = useRef<(() => void) | null>(null);

  const { removeUser } = useManageGroupUser();

  const columns = [
    columnHelper.accessor((row) => `${row.firstName} ${row.lastName}`, {
      id: 'name',
      cell: (info) => (
        <Link
          href={`/admin/lexid/users/${info.row.original.id}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          <Typography sx={{ fontWeight: 'bold' }}>
            {`${info.row.original.firstName} ${info.row.original.lastName}`}
          </Typography>
        </Link>
      ),
      header: () => t('common.name', 'Name'),
      size: 9999,
    }),
    columnHelper.accessor('email', {
      cell: (info) => info.getValue() ?? '',
      header: () => t('common.email', 'Email'),
      size: 9999,
    }),
    columnHelper.accessor('active', {
      cell: (info) => (
        <Badge
          variant='inline'
          badgeContent={info.getValue() ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
          color={info.getValue() ? 'success' : 'error'}
          sx={{ textTransform: 'uppercase' }}
        />
      ),
      header: () => t('common.active', 'Active'),
      size: 120,
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => (
        <ActionMenu
          id={`action_menu_${info.row.original.id}`}
          items={[
            ...(currentOrganizationId && id
              ? [
                {
                  key: `remove_${info.row.original.id}`,
                  label: t('groups.actions.removeUser'),
                  onClick: () =>
                    removeUser(currentOrganizationId, info.row.original.id || '', id, () => {
                      queryClient.invalidateQueries(['group_users', currentOrganizationId, id]);
                    }),
                },
              ]
              : []),
          ]}
        />
      ),
      header: () => '',
      size: 60,
    }),
  ];

  const users = React.useMemo(
    () => (usersQuery.data && usersQuery.data.pages ? usersQuery.data.pages.flatMap((x) => x.content) : []),
    [usersQuery.data]
  );

  return (
    <Stack>

      <Box
        sx={{
          width: '100%',
          backgroundColor: neutral[50],
          borderRadius: 2,
          px: 3,
          py: 5,
          mb: 4,
          display: 'flex',
          justifyContent: addingUsers ? 'space-between' : 'flex-end',
          alignItems: 'center',
        }}
      >
        {addingUsers ? (
          <>
            <QuickSearch id='group-users-search' placeholder={t('groups.details.searchUsers', 'Search users')} initialValue={searchQuery} onChange={(val) => setSearchQuery(val?.trim() || '')} />
            <Stack direction='row' spacing={2}>
              {step === 'select' ? (
                <>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => {
                      updateAddingUsers(false);
                      setSelectedCount(0);
                      setSearchQuery('');
                      setIsSubmitting(false);
                    }}
                  >
                    {t('common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    variant='contained'
                    disabled={selectedCount === 0}
                    onClick={() => { setStep('review'); setSearchQuery(''); }}
                  >
                    {t('common.continue', 'Continue')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={() => { setStep('select'); setSearchQuery(''); }}
                  >
                    {t('common.back', 'Back')}
                  </Button>
                  <Button
                    variant='contained'
                    loading={isSubmitting}
                    loadingPosition='end'
                    disabled={selectedCount === 0 || isSubmitting}
                    onClick={() => submitRef.current?.()}
                  >
                    {t('groups.details.addSelectedUsers', 'Add selected users')}
                  </Button>
                </>
              )}
            </Stack>
          </>
        ) : (
          <Button
            variant='contained'
            color='secondary'
            onClick={() => { updateAddingUsers(true); setSearchQuery(''); }}
          >
            {t('groups.details.addUsers', 'Add users')}
          </Button>
        )}
      </Box>
      {addingUsers ? (
        <AddUser
          searchQuery={searchQuery}
          step={step}
          onSuccess={() => {
            updateAddingUsers(false);
            setSelectedCount(0);
            setSearchQuery('');
          }}
          onSelectionChange={(count) => setSelectedCount(count)}
          onSubmittingChange={setIsSubmitting}
          submitRef={submitRef}
          existingUserIds={users.map((u) => u.id || '')}
        />
      ) : (
        <ListSection
          columns={columns}
          data={users}
          query={usersQuery}
        />
      )}
    </Stack>
  );
}
